// src/apple-nonce.js — pure helper for the Sign in with Apple nonce pair.
//
// The two values must be threaded through the native flow precisely:
//
//   Apple authorize()           ← receives the HASHED nonce.
//                                 Apple bakes that exact value into the
//                                 returned identityToken JWT as the
//                                 `nonce` claim.
//   sb.auth.signInWithIdToken() ← receives the RAW nonce.
//                                 Supabase server-side computes
//                                 SHA-256(raw) and compares the lowercase
//                                 hex digest against the JWT's `nonce`
//                                 claim. Match ⇒ session issued.
//
// Swapping the two (passing raw to Apple, or hashed to Supabase) produces
// an "invalid nonce" / token verification failure from Supabase.

function base64url(bytes) {
  // Browser/WebKit btoa expects a binary string.
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function toHex(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  return toHex(new Uint8Array(digest));
}

// Returns { raw, hashed }:
//   raw    — base64url of 32 random bytes (43 chars, URL-safe)
//   hashed — lowercase hex of SHA-256(raw)
export async function generateAppleNoncePair() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const raw = base64url(bytes);
  const hashed = await sha256Hex(raw);
  return { raw, hashed };
}

// Expose on window so the non-ESM JSX files can use it without imports.
if (typeof window !== 'undefined') {
  window.generateAppleNoncePair = generateAppleNoncePair;
}
